import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import useQuery from 'hooks/useQuery'
import { filterEmptyValues } from 'utils/helpers'
import {
  AggregatorDropdown,
  Button,
  Dropdown,
  TextInput,
} from 'components/common'
import { ORDER_TABLE_FIELDS } from 'utils/constants'
import { CustomObject } from 'types/common'
import StoreDropdown from 'components/userManagement/storeDropdown'
import { DateTime } from 'components/common/input/dateTime'
import { FILTER_FIELD_OPEN_ORDERS, OPEN_ORDER_STATUSES } from './utils'

const {
  ORDER_NO,
  ORDER_SHORTCODE,
  AGGREGATOR_CODE,
  INVOICE_NO,
  STORE_CODE,
  AREA,
  PREPARED_BY,
  STATUS,
  ON_HOLD_ORDER,
} = ORDER_TABLE_FIELDS

type Props = {
  toggleFilterForm: () => void
}

const OpenOrderFilterForm = ({ toggleFilterForm }: Props) => {
  const { getQuery, removeQuery, setMultipleQueries } = useQuery()
  const filters = getQuery(FILTER_FIELD_OPEN_ORDERS)

  const [resetFilter, setResetFilter] = useState(false)

  const { register, handleSubmit, setValue, reset } = useForm({
    defaultValues: filters,
  })

  const orderStatusOptions = useMemo(() => {
    const options = OPEN_ORDER_STATUSES.map((status) => ({
      label: status,
      value: status,
      disabled: false,
    }))
    options.unshift({ label: '--- NONE ---', value: 'NONE', disabled: false })
    return options
  }, [])

  const onFilterApply = (data: CustomObject) => {
    if (data?.[AGGREGATOR_CODE.value] === 'NONE') {
      delete data[AGGREGATOR_CODE.value]
    }
    if (data?.[STORE_CODE.value] === 'NONE') {
      delete data[STORE_CODE.value]
    }
    if (data?.[STATUS.value] === 'NONE') {
      delete data[STATUS.value]
    }
    if (data?.[ON_HOLD_ORDER.value] === 'NONE') {
      delete data[ON_HOLD_ORDER.value]
    }

    const filteredData = filterEmptyValues(data)

    if (Object.values(filteredData).length > 0) {
      setMultipleQueries({ [FILTER_FIELD_OPEN_ORDERS]: filteredData, page: 1 })
    } else {
      removeQuery(FILTER_FIELD_OPEN_ORDERS)
    }

    toggleFilterForm()
  }

  const cancelFilter = () => {
    reset(filters)
    setResetFilter((pre) => !pre)
    toggleFilterForm()
  }

  return (
    <div className="mt-2 rounded-lg border bg-white px-5 py-3">
      <div className="mx-auto max-w-5xl">
        <form onSubmit={handleSubmit(onFilterApply)}>
          <div className="grid grid-cols-2 gap-x-14 gap-y-3 md:grid-cols-3">
            {/* Order No */}
            <TextInput
              register={register}
              name={ORDER_NO.value}
              label={ORDER_NO.label}
            />

            {/* Invoice No */}
            <TextInput
              register={register}
              name={INVOICE_NO.value}
              label={INVOICE_NO.label}
            />

            {/* Area */}
            <TextInput
              register={register}
              name={AREA.value}
              label={AREA.label}
            />

            {/* Channel */}
            <AggregatorDropdown
              withNoneOption
              setValue={setValue}
              defaultValue={filters?.[AGGREGATOR_CODE.value] || resetFilter}
              name={AGGREGATOR_CODE.value}
              label={AGGREGATOR_CODE.label}
            />

            {/* Store */}
            <StoreDropdown
              withNoneOption
              setValue={setValue}
              defaultValue={filters?.[STORE_CODE.value] || resetFilter}
              name={STORE_CODE.value}
              label={STORE_CODE.label}
            />

            {/* Order Status — restricted to open order statuses */}
            <Dropdown
              name={STATUS.value}
              label={STATUS.label}
              setValue={setValue}
              defaultValue={filters?.[STATUS.value] || resetFilter}
              options={orderStatusOptions}
            />

            {/* Prepared By */}
            <TextInput
              register={register}
              name={PREPARED_BY.value}
              label={PREPARED_BY.label}
            />

            {/* Short Code */}
            <TextInput
              register={register}
              name={ORDER_SHORTCODE.value}
              label={ORDER_SHORTCODE.label}
            />

            {/* Date Range */}
            <DateTime label={'Orders From'} name={'from'} register={register} />
            <DateTime label={'Orders To'} name={'to'} register={register} />
          </div>

          <div className="my-4 flex gap-5">
            <Button buttonText="Apply Filters" className="w-32" />
            <Button
              buttonText="Cancel"
              variant="outline"
              type="button"
              onClick={cancelFilter}
              className="w-32"
            />
          </div>
        </form>
      </div>
    </div>
  )
}

export default OpenOrderFilterForm
